import React, { useEffect, useState, useCallback } from 'react';
import { useAssessmentsDataContext } from 'providers/assessments-data-provider';
import { Stack } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import Button from 'react-bootstrap/Button';
import SelfPacedQuestion from './self-paced-question';
import SelfPacedNav from './self-paced-nav';
import { markAssessmentStarted, submitAnswers } from '../../api/assessments-api';
import { useAuth } from '../../providers/auth-provider';
import LoadingSpinner from '../common/loading-spinner';

const MAX_QUESTIONS = 10;

function SelfPacedContainer({ type, time }) {
  const {
    selfPacedAssessments,
    selfCompetenciesAssessments,
    raterCompetenciesAssessments,
    pagedQuestions,
    setPagedQuestions,
    currentPage,
    setCurrentPage,
    pageQuestionsAnswered,
    setPageQuestionsAnswered,
    checkAnswers,
    checkAnswersForPage,
  } = useAssessmentsDataContext();
  const auth = useAuth();
  const { raterIndex } = useParams();
  const navigate = useNavigate();
  const [assessmentIntro, setAssessmentIntro] = useState(true);
  const [generalInformationIntro, setGeneralInformationIntro] = useState(false);
  const [isCompetenciesAssessment, setIsCompetenciesAssessment] = useState(false);
  const [maxPage, setMaxPage] = useState(0);
  const [initialized, setInitialized] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [buttonsDisabled, setButtonsDisabled] = useState(false);

  const getQuestions = useCallback(() => {
    const questions = [];
    for (let q = 0; q < pagedQuestions[currentPage].length; q += 1) {
      const question = pagedQuestions[currentPage][q];
      questions.push(<SelfPacedQuestion key={question.questionNumber} question={question} />);
      if (question.questionNumber === 1 && !question.registered && !question.submitting) {
        question.submitting = true;
        markAssessmentStarted(auth, question.assessmentId).then(() => {
          question.registered = true;
          question.submitting = false;
        });
      }
    }
    return questions;
  }, [auth, currentPage, pagedQuestions]);

  const onNavClick = useCallback(
    (event, forward) => {
      if (forward) {
        if (pageQuestionsAnswered) {
          setSubmitting(true);
          setButtonsDisabled(true);
          const pageQuestions = pagedQuestions[currentPage];
          const assessmentCompleted = pageQuestions[pageQuestions.length - 1].lastQuestion;
          submitAnswers(auth, pageQuestions, assessmentCompleted).then(() => {
            setSubmitting(false);
            if (currentPage < maxPage) {
              const nextPage = currentPage + 1;
              setCurrentPage(nextPage);
              checkAnswersForPage(nextPage);
              if (pagedQuestions[nextPage][0].instructions) {
                setGeneralInformationIntro(true);
              }
              const layoutDiv = document.getElementById('self-paced-container');
              layoutDiv.scroll(0, 0);
            } else {
              navigate('/');
            }
          });
        }
      } else if (currentPage > 0) {
        const prevPage = currentPage - 1;
        setCurrentPage(currentPage - 1);
        checkAnswersForPage(prevPage);
      }
      event.target.blur();
    },
    [currentPage, pageQuestionsAnswered, pagedQuestions, auth, maxPage, setCurrentPage, checkAnswersForPage, navigate]
  );

  const getPage = useCallback(() => {
    if (pagedQuestions.length) {
      return <Stack className="m-5 w-75 mx-auto bg-white self-paced-content">{getQuestions()}</Stack>;
    }
    return undefined;
  }, [pagedQuestions.length, getQuestions]);

  useEffect(() => {
    if (!initialized) {
      let assessments;
      switch (type) {
        case 'self-competencies':
          assessments = selfCompetenciesAssessments;
          break;
        case 'rater-competencies':
          assessments = [raterCompetenciesAssessments[raterIndex]];
          break;
        default:
          assessments = selfPacedAssessments;
          break;
      }
      const pages = [];
      for (let s = 0; s < assessments.length; s += 1) {
        let questionNumber = 1;
        let page = [];
        let pageItems = 0;
        const startingPage = pages.length;
        const assessment = assessments[s];
        const { questions } = assessment;
        for (let q = 0; q < questions.length; q += 1) {
          const dq = {
            questionNumber,
            assessmentId: assessment.id,
            question: questions[q],
            answer: questions[q].answer,
          };
          if (questionNumber === 1) {
            dq.instructions = assessment.instructions;
          }
          if (assessment.started) {
            dq.registered = true;
          }
          if (q === questions.length - 1) {
            dq.lastQuestion = true;
            dq.startingPage = startingPage;
          }
          questionNumber += 1;
          page.push(dq);
          pageItems += 1;
          if (pageItems % MAX_QUESTIONS === 0) {
            pages.push(page);
            page = [];
          }
        }
        if (page.length) {
          pages.push(page);
        }
      }
      setCurrentPage(0);
      setPagedQuestions(pages);
      setMaxPage(pages.length - 1);
      checkAnswers(pages[0]);
      if (type.endsWith('competencies')) {
        setIsCompetenciesAssessment(true);
      }
      setInitialized(true);
    }
  }, [
    currentPage,
    checkAnswers,
    initialized,
    pagedQuestions,
    selfPacedAssessments,
    selfCompetenciesAssessments,
    raterCompetenciesAssessments,
    setCurrentPage,
    setPageQuestionsAnswered,
    setPagedQuestions,
  ]);

  return (
    <>
      {submitting && <LoadingSpinner />}
      {!assessmentIntro && !generalInformationIntro && (
        <div className="w-100 mx-auto bg-gray-200 pb-4 overflow-auto" id="self-paced-container">
          {getPage()}
          <SelfPacedNav
            currentPage={currentPage}
            maxPage={maxPage}
            onNavClick={onNavClick}
            buttonsDisabled={buttonsDisabled}
          />
        </div>
      )}
      {assessmentIntro && !generalInformationIntro && (
        <div className="d-flex flex-column bg-light w-75 mx-auto pt-4">
          <div className="d-flex flex-row justify-content-center text-larger text-bolder">
            {isCompetenciesAssessment ? (
              <>
                This assessment, on average, takes {time} minutes to complete. Please read the instructions carefully.
              </>
            ) : (
              <>
                During this assessment, you will be asked to respond to several different types of questions. This
                assessment, on average, takes {time} minutes to complete. There are three sections in this assessment,
                with some longer than others. Please read the instructions for each section carefully.
              </>
            )}
          </div>
          <div className="d-flex flex-row justify-content-start pt-4 text-larger text-bolder">Click NEXT to begin</div>
          <div className="d-flex flex-row justify-content-center pt-4 mb-5">
            <Button
              className="btn-assessment-primary mb-5"
              style={{ width: '125px' }}
              onClick={() => {
                setAssessmentIntro(false);
                if (currentPage === 0) {
                  setGeneralInformationIntro(true);
                }
              }}
            >
              Next
            </Button>
          </div>
        </div>
      )}
      {!assessmentIntro && generalInformationIntro && pagedQuestions[currentPage][0].instructions && (
        <div className="d-flex flex-column w-75 mx-auto pt-4">
          <div className="d-flex flex-row justify-content-center text-larger text-bolder">
            {pagedQuestions[currentPage][0].instructions}
          </div>
          <div className="d-flex flex-row justify-content-start pt-4 text-larger text-bolder">Click NEXT to begin</div>
          <div className="d-flex flex-row justify-content-center pt-4 mb-5">
            <Button
              className="btn-assessment-primary mb-5"
              style={{ width: '125px' }}
              onClick={() => setGeneralInformationIntro(false)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

export default SelfPacedContainer;
